import { upsertPartTypeMetricLimits } from "../repositories/MetricLimitsRepository";
import { ErrorModal } from "@dexteel/mesf-core";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
  METRIC_LIMITS_INITIAL_VALUES,
  PartTypeMetricLimits,
} from "../models/metricLimits.models";

interface Props {
  partType: PartTypeMetricLimits | null;
  onHide: ({
    shouldUpdate,
    close,
  }: {
    shouldUpdate: boolean;
    close: boolean;
  }) => void;
}

const integerValidationRules = {
  validate: {
    isInteger: (value: string | number) =>
      Number.isInteger(Number(value)) || "Must be a whole number",
    isPositive: (value: string | number) =>
      Number(value) >= 0 || "Must be a positive number",
  },
};

const IntegerField = ({
  name,
  control,
  label,
}: {
  name: keyof PartTypeMetricLimits;
  control: ReturnType<typeof useForm<PartTypeMetricLimits>>["control"];
  label: string;
}) => (
  <Controller
    name={name}
    control={control}
    rules={integerValidationRules}
    render={({ field, fieldState: { error } }) => (
      <TextField
        {...field}
        label={label}
        variant="outlined"
        type="number"
        size="small"
        fullWidth
        error={!!error}
        helperText={error?.message}
      />
    )}
  />
);

export const MetricLimitsDetails = ({ partType, onHide }: Props) => {
  const [message, setNotificationMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    reset,
    handleSubmit,
    control,
    formState: { isDirty, isValid },
  } = useForm<PartTypeMetricLimits>({
    mode: "onBlur",
  });

  const onSubmit: SubmitHandler<PartTypeMetricLimits> = async (data) => {
    setIsLoading(true);
    const resp = await upsertPartTypeMetricLimits(data);
    if (resp.ok) {
      setNotificationMessage("The metric limits were saved successfully.");
      onHide({ shouldUpdate: true, close: false });
    } else {
      setError(resp.message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    reset(partType || METRIC_LIMITS_INITIAL_VALUES);
    setIsLoading(false);
  }, [partType, reset]);

  return (
    <>
      {partType === null ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography>Select a part type to edit its metric limits</Typography>
        </Box>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} sx={{ p: 2.5 }}>
            {/* Part Type Name - Read Only */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {partType.PartTypeName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Configure the warning and alert limits for heats
              </Typography>
            </Grid>

            {/* Campaign Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Campaign (Heats Since Last Action)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <IntegerField
                      name="CampaignWarningLimit"
                      control={control}
                      label="Warning Limit"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <IntegerField
                      name="CampaignAlertLimit"
                      control={control}
                      label="Alert Limit"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Partial Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Partial (Heats Since Last Repair)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <IntegerField
                      name="PartialWarningLimit"
                      control={control}
                      label="Warning Limit"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <IntegerField
                      name="PartialAlertLimit"
                      control={control}
                      label="Alert Limit"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Total Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Total (Lifetime Heats)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <IntegerField
                      name="TotalWarningLimit"
                      control={control}
                      label="Warning Limit"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <IntegerField
                      name="TotalAlertLimit"
                      control={control}
                      label="Alert Limit"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Buttons */}
            <Grid item xs={6} md={4} sx={{ ml: "auto" }}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => onHide({ shouldUpdate: false, close: true })}
              >
                CLOSE
              </Button>
            </Grid>
            <Grid item xs={6} md={4}>
              <Button
                fullWidth
                startIcon={
                  isLoading ? <CircularProgress size="1rem" /> : undefined
                }
                disabled={isLoading || !isDirty || !isValid}
                variant="contained"
                color="primary"
                type="submit"
              >
                SAVE
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
      <NotificationSnackBar
        message={message}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
